import React, { useState } from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { cn } from "../../lib/utils";
import { scopeProgressPct } from "../../lib/homeTaskPreview";
import AddTaskDrawer from "./AddTaskDrawer";
import EditableTaskRow from "./EditableTaskRow";

/** Scope workboard — expand a scope, checklist drives progress. */
export default function WorkspaceScopeWorkboard({
  scopes,
  selectedScopeId,
  onSelectScope,
  onToggle,
  onAdd,
  onUpdate,
  onDelete,
}) {
  const [addOpen, setAddOpen] = useState(false);
  const active = scopes.find((s) => s.id === selectedScopeId) || scopes[0];

  if (!scopes.length) {
    return (
      <div data-testid="workspace-tasks-v3">
        <p className="text-sm text-[var(--t-meta)]">No scope items yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="workspace-tasks-v3">
      <div className="flex items-baseline justify-between px-0.5">
        <div className="label-eyebrow">Scope workboard</div>
        <span className="text-[11px] text-[var(--t-meta)]">{scopes.length} scopes</span>
      </div>
      {scopes.map((scope) => {
        const open = selectedScopeId === scope.id;
        const pct =
          scope.progressPct != null ? scope.progressPct : scopeProgressPct(scope);
        const openCount = scope.tasks.filter((t) => !t.done).length;
        return (
          <div key={scope.id} className="min-w-0">
            <button
              type="button"
              onClick={() => onSelectScope(scope.id)}
              className="flex w-full items-center gap-3 py-1.5 text-left"
              data-testid={`workspace-tasks-v3-scope-${scope.id}`}
            >
              {open ? (
                <ChevronDown size={14} className="shrink-0 text-[var(--t-accent)]" />
              ) : (
                <ChevronRight size={14} className="shrink-0 text-[var(--t-meta)]" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{scope.title}</div>
                <div className="mt-0.5 text-[11px] text-[var(--t-meta)]">
                  {openCount} open · {pct}%
                </div>
              </div>
              <div className="h-1.5 w-16 overflow-hidden rounded-full bg-black/5 sm:w-24">
                <div
                  className="h-full rounded-full bg-[var(--t-accent)]"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </button>
            {open ? (
              <div className="mt-1 space-y-0.5 border-l border-[var(--t-accent)]/25 pl-3">
                {scope.tasks.length === 0 ? (
                  <p className="px-1 py-2 text-sm text-[var(--t-meta)]">No tasks yet — add the first one.</p>
                ) : null}
                {scope.tasks.map((t) => (
                  <EditableTaskRow
                    key={t.id}
                    task={t}
                    onToggle={onToggle}
                    onUpdate={(patch) => onUpdate?.(t.id, patch)}
                    onDelete={onDelete ? () => onDelete(t.id) : undefined}
                    data-testid={`workspace-task-v3-${t.id}`}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className={cn(
                    "mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]",
                  )}
                  data-testid="workspace-tasks-v3-add"
                >
                  <Plus size={12} /> Add task
                </button>
              </div>
            ) : null}
          </div>
        );
      })}

      <AddTaskDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        scopeLabel={active?.title}
        onSubmit={(payload) => onAdd?.(payload)}
        testId="workspace-add-task"
      />
    </div>
  );
}
